import { createContext, FC, ReactNode, useContext, useState } from 'react';
import { CompanyRealtimeAccess } from '../types';
import { RequestStatus } from 'types/RequestStatus';

export interface CompanyLevelFormState {
	providerCode: string;
	start: Date;
	end: Date;
	realtimeOnly: boolean;
	applicationId: string;
}

export type CompanyLevelFormContextType = {
	formState: CompanyLevelFormState;
	setFormState: React.Dispatch<React.SetStateAction<CompanyLevelFormState>>;
	providerAccessLogs: CompanyRealtimeAccess[];
	setProviderAccessLogs: React.Dispatch<
		React.SetStateAction<CompanyRealtimeAccess[]>
	>;
	accessEntriesStatus: RequestStatus;
	setAccessEntriesStatus: React.Dispatch<React.SetStateAction<RequestStatus>>;
};

const defaultFormState: CompanyLevelFormState = {
	providerCode: '',
	start: new Date(),
	end: new Date(),
	realtimeOnly: false,
	applicationId: '',
};

export const CompanyLevelFormContext =
	createContext<CompanyLevelFormContextType | null>(null);

const CompanyLevelFormContextProvider: FC<{ children: ReactNode }> = ({
	children,
}) => {
	const [formState, setFormState] =
		useState<CompanyLevelFormState>(defaultFormState);
	const [providerAccessLogs, setProviderAccessLogs] = useState<
		CompanyRealtimeAccess[]
	>([]);
	const [accessEntriesStatus, setAccessEntriesStatus] =
		useState<RequestStatus>(RequestStatus.UNLOADED);

	return (
		<CompanyLevelFormContext.Provider
			value={{
				formState,
				setFormState,
				providerAccessLogs,
				setProviderAccessLogs,
				accessEntriesStatus,
				setAccessEntriesStatus,
			}}
		>
			{children}
		</CompanyLevelFormContext.Provider>
	);
};

export function useCompanyLevelFormContext() {
	const context = useContext(CompanyLevelFormContext);
	if (!context) {
		throw new Error(
			'useCompanyLevelFormContext must be used within CompanyLevelFormContextProvider',
		);
	}
	return context;
}

export default CompanyLevelFormContextProvider;
